const tagSet = (tags: string): Record<string, boolean> => {
  const res: Record<string, boolean> = {};
  for (const t of tags.split(',')) {
    res[t] = true;
  }
  return res;
};

/**
 * SanitizingHtmlSerializer serializes a DOM fragment, stripping out any unsafe elements and unsafe
 * attributes.
 */
export class SanitizingSvgSerializer {
  // Explicitly track if something was stripped, to avoid accidentally warning of sanitization just
  // because characters were re-encoded.
  private static readonly VALID_ELEMENTS: Record<string, boolean> = tagSet('svg,symbol,path,defs,circle,g,ellipse,style,rect');

  private static readonly VALID_ATTRS: Record<string, boolean> = tagSet(
    'id,fill,d,viewbox,fill-rule,clip-rule,stroke,stroke-miterlimit,stroke-linecap,cx,cy,' +
    'r,stroke-linejoin,transform,opacity,stroke-dasharray,stroke-width,rx,ry,class,x,y,width,height');

  private static readonly SKIP_TRAVERSING_CONTENT_IF_INVALID_ELEMENTS: Record<string, boolean> = tagSet('script,style,template');

  // Regular Expressions for parsing tags and attributes
  private static readonly SURROGATE_PAIR_REGEXP: RegExp = /[\uD800-\uDBFF][\uDC00-\uDFFF]/g;
  // ! to ~ is the ASCII range.
  private static readonly NON_ALPHANUMERIC_REGEXP: RegExp = /([^#-~ |!])/g;

  public sanitizedSomething: boolean = false;
  private buf: Array<string> = [];

  sanitizeChildren(el: Element): string {
    let current: Node = el.firstChild;
    let traverseContent = true;
    while (current !== null) {
      if (current.nodeType === Node.ELEMENT_NODE) {
        traverseContent = this.startElement(current as Element);
      } else if (current.nodeType === Node.TEXT_NODE) {
        this.chars(current.nodeValue);
      } else {
        // Strip non-element, non-text nodes.
        this.sanitizedSomething = true;
      }
      if (traverseContent && current.firstChild !== null) {
        current = current.firstChild;
        continue;
      }
      while (current !== null) {
        // Leaving the element. Walk up and to the right, closing tags as we go.
        if (current.nodeType === Node.ELEMENT_NODE) {
          this.endElement(current as Element);
        }

        const next = this.checkClobberedElement(current, current.nextSibling);

        if (next !== null) {
          current = next;
          break;
        }

        current = this.checkClobberedElement(current, current.parentNode);
      }
    }
    return this.buf.join('');
  }

  checkClobberedElement(node: Node, nextNode: Node): Node {

    if (nextNode !== null &&
      (node.compareDocumentPosition(nextNode) &
        Node.DOCUMENT_POSITION_CONTAINED_BY) === Node.DOCUMENT_POSITION_CONTAINED_BY) {
      throw new Error(`Failed to sanitize html because the element is clobbered: ${
        (node as Element).outerHTML}`);
    }
    return nextNode;
  }

  /**
   * Sanitizes an opening element tag (if valid) and returns whether the element's contents should
   * be traversed. Element content must always be traversed (even if the element itself is not
   * valid/safe), unless the element is one of `SKIP_TRAVERSING_CONTENT_IF_INVALID_ELEMENTS`.
   *
   * @param element The element to sanitize.
   * @return True if the element's contents should be traversed.
   */
  private startElement(element: Element): boolean {
    const tagName = element.nodeName.toLowerCase();
    // eslint-disable-next-line no-prototype-builtins
    if (!SanitizingSvgSerializer.VALID_ELEMENTS.hasOwnProperty(tagName)) { // TODO replace hasOwnProperty with Array.prototype.includes
      this.sanitizedSomething = true;
      // eslint-disable-next-line no-prototype-builtins
      return !SanitizingSvgSerializer.SKIP_TRAVERSING_CONTENT_IF_INVALID_ELEMENTS.hasOwnProperty(tagName);
    }
    this.buf.push('<');
    this.buf.push(tagName);
    const elAttrs = element.attributes;
    for (let i = 0; i < elAttrs.length; i++) {
      const elAttr = elAttrs.item(i);
      const attrName = elAttr.name;
      const lower = attrName.toLowerCase();
      // eslint-disable-next-line no-prototype-builtins
      if (!SanitizingSvgSerializer.VALID_ATTRS.hasOwnProperty(lower)) {
        this.sanitizedSomething = true;
        continue;
      }
      const value = elAttr.value;
      this.buf.push(' ', attrName, '="', this.encodeEntities(value), '"');
    }
    this.buf.push('>');
    return true;
  }

  private endElement(current: Element): void {
    const tagName = current.nodeName.toLowerCase();
    // eslint-disable-next-line no-prototype-builtins
    if (SanitizingSvgSerializer.VALID_ELEMENTS.hasOwnProperty(tagName)) {
      this.buf.push('</');
      this.buf.push(tagName);
      this.buf.push('>');
    }
  }

  private chars(chars: string): void {
    this.buf.push(this.encodeEntities(chars));
  }

  /**
   * Escapes all potentially dangerous characters, so that the
   * resulting string can be safely inserted into attribute or
   * element text.
   *
   * @param value
   */
  private encodeEntities(value: string): string {
    return value.replace(/&/g, '&amp;')
      .replace(
        SanitizingSvgSerializer.SURROGATE_PAIR_REGEXP,
        (match: string): string => {
          const hi = match.charCodeAt(0);
          const low = match.charCodeAt(1);
          return '&#' + (((hi - 0xD800) * 0x400) + (low - 0xDC00) + 0x10000) + ';';
        })
      .replace(
        SanitizingSvgSerializer.NON_ALPHANUMERIC_REGEXP,
        (match: string): string => '&#' + match.charCodeAt(0) + ';')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}
